import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const stats = [
    { value: 1200, suffix: "+", label: "Properties Cleaned" },
    { value: 240, suffix: "+", label: "5-Star Reviews" },
    { value: 8, suffix: " yrs", label: "Of Experience" },
    { value: 100, suffix: "%", label: "Satisfaction Guarantee" },
];

function Counter({ to, suffix, duration = 1.8 }) {
    const [val, setVal] = useState(0);
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-50px" });

    useEffect(() => {
        if (!inView) return;
        let raf;
        const start = performance.now();
        const tick = (now) => {
            const t = Math.min((now - start) / (duration * 1000), 1);
            const eased = 1 - Math.pow(1 - t, 3);
            setVal(Math.floor(eased * to));
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, to, duration]);

    return (
        <span ref={ref}>
            {val}
            {suffix}
        </span>
    );
}

export default function Stats() {
    return (
        <section
            data-testid="stats-section"
            className="relative py-20 bg-[#1E88E5] border-y border-white/10 overflow-hidden"
        >
            {/* subtle pattern */}
            <div
                className="absolute inset-0 opacity-[0.07] pointer-events-none"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 14px)",
                }}
            />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
                {stats.map((s, i) => (
                    <motion.div
                        key={s.label}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.08 }}
                        className="border-l-2 border-white/40 pl-5"
                        data-testid={`stat-${i}`}
                    >
                        <div className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl text-white leading-none">
                            <Counter to={s.value} suffix={s.suffix} />
                        </div>
                        <div className="mt-3 text-xs uppercase tracking-[0.2em] text-white/90 font-semibold">
                            {s.label}
                        </div>
                    </motion.div>
                ))}
            </div>
        </section>
    );
}
